import React, { useMemo } from 'react'
import './order-table.scss'
import { AutoResizer, WithScrollLoad } from '@/utils/utils-widget'
import { Table, Row } from 'antd'
import { $post } from '@/api'
import { IPagination } from '@/model/types/common'
import { useRequest } from 'ahooks'
import $msg from '@/utils/message'
import { ICashierOrder } from './type'
import { ColumnsType } from 'antd/lib/table'
import Big from 'big.js'
import useBemClass from '@/utils/hooks/useBEMClass'
import { Column } from '@/components/layout/layout'
import { IUser } from '@/model/types/user'
import { IOrderCashierFilterData } from '../../order-filter/order-filter'
import { showLayer } from '@/utils/layer'
import CashierOrderDetail from '../order-detail/order-detail'
import { observer } from 'mobx-react'
import { cashierOrderStore } from '../../store'
import WordHighLight from '@/components/word-highlight/word-highlight'
import { staffStore } from '@/store/staff'

interface CashierOrderTableProps extends IOrderCashierFilterData {
    date: string[]
    keyWord: string
}

const CashierOrderTable: React.FC<CashierOrderTableProps> = props => {
    const cls = useBemClass('cashier-order-table')
    const hasShopDataPermission = !!staffStore.canIUse(3)
    const noPermissionPlaceholder = '****'

    // 默认支付方式id数组
    const paymentIds = useMemo(() => {
        return props.paymentList
            .filter(item => !item.is_custom_pay_type)
            .map(p => {
                return p.id
            })
    }, [props.paymentList])

    // 自定义支付方式id数组
    const customPaymentIds = useMemo(() => {
        return props.paymentList
            .filter(item => item.is_custom_pay_type)
            .map(p => {
                return p.id
            })
    }, [props.paymentList])

    const CashierOrderDataReq = useRequest<{
        sum_sale_count: number
        sum_sale_customer_count: number
        sum_sale_amount: number
        sum_sale_goods_amount: number
        customer_price: number
    }>(
        () =>
            hasShopDataPermission
                ? $post({
                      url: '/finance/getcashierorderstatistics',
                      params: {
                          from_date: props.date[0],
                          to_date: props.date[1],
                      },
                  })
                : Promise.resolve(void 0),
        {
            refreshDeps: [props.date, cashierOrderStore.refresh, hasShopDataPermission],
        },
    )

    const requestHandle = useRequest<
        {
            list: ICashierOrder.Order[]
            pagination: IPagination
        },
        {
            orders: ICashierOrder.Order[]
            pagination: IPagination
        }
    >(
        val => {
            return $post({
                url: '/order/orderdatalist',
                params: {
                    limit: 20,
                    page: val?.pagination?.page ? val.pagination.page + 1 : 1,
                    start_date: props.date[0],
                    end_date: props.date[1],
                    keyword: props.keyWord || void 0,
                    order_status: props.orderStatus,
                    payment_list: paymentIds.length ? [...new Set(paymentIds)] : void 0,
                    custom_payment_ids: customPaymentIds.length ? [...new Set(customPaymentIds)] : void 0,
                    staff_ids: props.staffList.length ? props.staffList.map(s => s.id) : void 0,
                },
            })
        },
        {
            isNoMore: data => {
                if (data?.pagination) {
                    return data?.pagination.page === data?.pagination.page_count
                } else {
                    return false
                }
            },
            loadMore: true,
            refreshDeps: [
                props.date,
                props.keyWord,
                props.orderStatus,
                props.paymentList,
                props.staffList,
                cashierOrderStore.refresh,
            ],
            formatResult: data => {
                return {
                    list: data.orders,
                    pagination: data.pagination,
                }
            },
            onError: e => {
                $msg('error', e.message)
            },
        },
    )

    const userInfo = (user: IUser) => (
        <Row data-align-items-center>
            <img
                style={{
                    border: `1px solid ${user.gender === 2 ? '#ee5b5b' : '#3c90ff'}`,
                }}
                className={cls('avatar')}
                src={user.avatar}
                alt=""
            />
            <Column>
                <div className={cls('nickname')}>{user.nickname}</div>
                <div className={cls('realname')}>
                    (<WordHighLight keyword={props.keyWord} word={user.realname}></WordHighLight>)
                </div>
            </Column>
        </Row>
    )

    const customUser = (user: { phone: string; name: string }) => (
        <Row data-align-items-center>
            <Column>
                <div className={cls('nickname')}>
                    (<WordHighLight keyword={props.keyWord} word={user.name || '-'}></WordHighLight>)
                </div>
                <div className={cls('realname')}>{user.phone}</div>
            </Column>
        </Row>
    )

    const columns: ColumnsType<ICashierOrder.Order> = [
        {
            title: '#',
            key: 'index',
            align: 'center',
            width: 40,
            render: (value, record, index) => index + 1,
        },
        {
            title: '时间',
            width: 110,
            dataIndex: 'create_time',
        },
        {
            title: '订单编号',
            dataIndex: 'order_no',
            width: 120,
            render: val => <WordHighLight keyword={props.keyWord} word={val}></WordHighLight>,
        },
        {
            title: '会员',
            render: (v, r) =>
                Object.keys(r.member).length
                    ? Object.keys(r.member.user).length
                        ? userInfo(r.member.user)
                        : customUser({
                              phone: r.member.phone,
                              name: r.member.name,
                          })
                    : '-',
        },
        {
            title: '商品总价',
            dataIndex: 'subtotal',
            width: 130,
            align: 'right',
            sorter: (a, b) => a.subtotal - b.subtotal,
            render: v => `¥${+v}`,
        },
        {
            title: '总优惠',
            align: 'right',
            render: (v, r) => `¥${+Big(r.subtotal).minus(r.actual_payment)}`,
        },
        {
            title: '实付款',
            width: 130,
            dataIndex: 'actual_payment',
            sorter: (a, b) => a.actual_payment - b.actual_payment,
            align: 'right',
            render: (v, r) => {
                const refundMoney = +r.payment_refunds.reduce((pre, next) => {
                    return pre.plus(next.refund_money)
                }, Big(0))

                return (
                    <Column>
                        {`¥${v}`}
                        {!!refundMoney && (
                            <div data-font-size-12 data-color-ff4343>
                                {refundMoney === r.actual_payment ? '已全额退款' : `已退款¥${refundMoney}`}
                            </div>
                        )}
                    </Column>
                )
            },
        },
        {
            title: '支付方式',
            width: 160,
            render: (v, r) =>
                r.payments.map(p => (
                    <Row data-justify-content-space-between data-align-item-center key={p.payment_type}>
                        <span>{p.custom_payment?.name ? p.custom_payment?.name : p.payment_type_text}</span>
                        <span>¥{p.payment_amount}</span>
                    </Row>
                )),
        },
        {
            title: '收银员',
            render: (val, record) => userInfo(record.creator.user),
        },
        {
            title: '操作',
            key: 'is_draft',
            width: 74,
            align: 'center',
            render: (val, record) => (
                <div
                    className="action"
                    onClick={() => {
                        showLayer(<CashierOrderDetail orderNo={record.order_no} />)
                    }}
                >
                    详情
                </div>
            ),
        },
    ]

    return (
        <div className="table-container" data-position-relative>
            <AutoResizer>
                {({ height }) => {
                    return (
                        <WithScrollLoad data-expaned data-overflow-hidden scrollloadhandle={requestHandle}>
                            <Table
                                rowKey={record => record.id}
                                loading={requestHandle.loading || requestHandle.loadingMore || void 0}
                                bordered
                                pagination={false}
                                columns={columns}
                                dataSource={requestHandle.data?.list}
                                scroll={{
                                    x: 'max-content',
                                    y: height - 110,
                                }}
                            />
                        </WithScrollLoad>
                    )
                }}
            </AutoResizer>
            <div className={cls('bottom')}>
                <div className="left" data-margin-left-16 data-margin-top-9>
                    订单量：
                </div>
                <div className="count">
                    {hasShopDataPermission ? CashierOrderDataReq.data?.sum_sale_count : noPermissionPlaceholder}
                </div>
                <div className="left" data-margin-left-32 data-margin-top-9>
                    客单价：¥
                </div>
                <div className="count">
                    {hasShopDataPermission ? CashierOrderDataReq.data?.customer_price : noPermissionPlaceholder}
                </div>
                <div className="line" />
                <div className="left" data-margin-top-9>
                    总销售额：¥
                </div>
                <div className="count">
                    {hasShopDataPermission ? CashierOrderDataReq.data?.sum_sale_amount : noPermissionPlaceholder}
                </div>
            </div>
        </div>
    )
}

export default observer(CashierOrderTable)
